import {
  Modal,
  CustomModalLayout,
  Loader,
  TextButton,
} from "@wix/design-system";
import { useRecreateModal } from "./useRecreateModal";
import { PAGE_AREA } from "../../../bi/constants";
import { RecreateBottomSheet } from "./RecreateBottomSheet";
import { RecreateForm } from "./RecreateForm";

interface RecreateModalProps {
  isOpen: boolean;
  onGenerate: (url: string) => Promise<boolean>;
  onClose: () => void;
  validateUrl: (url: string) => boolean;
  content: RecreateModalContent;
  pageArea: PAGE_AREA;
  isMobile?: boolean;
}

interface RecreateModalContent {
  title: string;
  subtitle: string;
  image: string;
  inputLabel: string;
  inputPlaceholder: string;
  primaryButtonText: string;
  secondaryButtonText: string;
  regexValidationError: string;
  apiValidationError: string;
  helperText?: string;
  sideActionsText?: string;
  sideActionsUrl?: string;
}

export const RecreateModal = ({
  isOpen,
  onGenerate,
  onClose,
  content,
  validateUrl,
  pageArea,
  isMobile = false,
}: RecreateModalProps) => {
  const {
    title,
    subtitle,
    image,
    inputLabel,
    inputPlaceholder,
    primaryButtonText,
    secondaryButtonText,
    regexValidationError,
    apiValidationError,
    helperText,
    sideActionsText,
    sideActionsUrl,
  } = content;

  const {
    isGenerating,
    url,
    urlError,
    handleGenerateSite,
    handleCancel,
    handleUrlChange,
  } = useRecreateModal({
    onGenerate,
    onClose,
    validateUrl,
    regexValidationError,
    apiValidationError,
    pageArea,
  });

  const formContent = (
    <RecreateForm
      image={image}
      isMobile={isMobile}
      inputLabel={inputLabel}
      urlError={urlError}
      helperText={helperText}
      url={url}
      handleUrlChange={handleUrlChange}
      inputPlaceholder={inputPlaceholder}
    />
  );

  const sideActions = (
    <TextButton
      size="small"
      onClick={() => window.open(sideActionsUrl, "_blank")}
    >
      {sideActionsText}
    </TextButton>
  );

  if (isMobile) {
    return (
      <RecreateBottomSheet
        isOpen={isOpen}
        onClose={onClose}
        title={title}
        subtitle={subtitle}
        formContent={formContent}
        primaryButtonText={primaryButtonText}
        secondaryButtonText={secondaryButtonText}
        isGenerating={isGenerating}
        handleGenerateSite={handleGenerateSite}
        handleCancel={handleCancel}
        sideActions={sideActions}
      />
    );
  }

  return (
    <Modal isOpen={isOpen}>
      <CustomModalLayout
        width="600px"
        title={title}
        style={{ borderRadius: "12px" }}
        subtitle={subtitle}
        closeButtonProps={{
          skin: "dark",
          onClick: handleCancel,
        }}
        content={formContent}
        primaryButtonText={
          isGenerating ? <Loader size="tiny" /> : primaryButtonText
        }
        primaryButtonOnClick={handleGenerateSite}
        secondaryButtonText={secondaryButtonText}
        secondaryButtonOnClick={handleCancel}
        sideActions={sideActions}
      />
    </Modal>
  );
};
